import {
  Download,
  Grape,
  List,
  LockKeyhole,
  MapPin,
  Menu,
  Plus,
  Trash2,
  UtensilsCrossed,
  WineOff,
  X,
} from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { offlinePersistence } from "../lib/offline";
import { Link, useLocation } from "../lib/router";
import { forgetSecureDevice, lockSecureReplica } from "../lib/vault-sync";
import { OfflineStatus } from "./OfflineStatus";
import { PairingAdviceModal } from "./PairingAdviceModal";

interface AppShellProps {
  children: ReactNode;
  onExport?: () => void;
  exporting?: boolean;
}

export function AppShell({ children, onExport, exporting }: AppShellProps) {
  const { pathname } = useLocation();
  const onInventory = pathname === "/" || pathname === "/epuises";
  const inventoryActive =
    pathname === "/" || pathname.startsWith("/vins/");
  const exhaustedActive = pathname === "/epuises";
  const mobileInventoryActive = inventoryActive;
  const locationsActive = pathname === "/emplacements";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pairingOpen, setPairingOpen] = useState(false);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileMenuOpen]);

  const handleForgetDevice = async () => {
    const snapshot = await offlinePersistence.getSnapshot();
    const pending = snapshot.pending_count > 0
      ? ` ${snapshot.pending_count} action${snapshot.pending_count > 1 ? "s" : ""} en attente ${snapshot.pending_count > 1 ? "resteront" : "restera"} dans le coffre chiffré.`
      : "";
    if (!window.confirm(
      `Oublier cet appareil retire son ouverture automatique et révoque sa session serveur.${pending} La copie locale chiffrée et le coffre distant restent intacts. Continuer ?`,
    )) return;
    await forgetSecureDevice();
  };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#contenu">
        Aller au contenu
      </a>
      <header className="site-header">
        <div className="header-inner">
          <Link className="brand" to="/" aria-label="Ma Cave, inventaire">
            <span className="brand-mark" aria-hidden="true">
              <Grape size={21} strokeWidth={1.8} />
            </span>
            <strong>Ma Cave</strong>
          </Link>
          <nav className="main-navigation" aria-label="Navigation principale">
            <Link className={inventoryActive ? "active" : ""} to="/" aria-current={inventoryActive ? "page" : undefined}>
              <List size={17} aria-hidden="true" /> Inventaire
            </Link>
            <Link className={exhaustedActive ? "active" : ""} to="/epuises" aria-current={exhaustedActive ? "page" : undefined}>
              <WineOff size={17} aria-hidden="true" /> Épuisés
            </Link>
            <Link className={locationsActive ? "active" : ""} to="/emplacements" aria-current={locationsActive ? "page" : undefined}>
              <MapPin size={17} aria-hidden="true" /> Emplacements
            </Link>
          </nav>
          <nav className="header-actions" aria-label="Actions principales">
            {onInventory && onExport ? (
              <button
                className="button button-ghost header-export"
                type="button"
                aria-label={exporting ? "Préparation de l’export" : "Exporter mes données en deux fichiers CSV"}
                onClick={onExport}
                disabled={exporting}
              >
                <Download size={17} aria-hidden="true" />
                <span>{exporting ? "Préparation…" : "Exporter mes données"}</span>
              </button>
            ) : null}
            <button
              className="button button-secondary header-pairing"
              type="button"
              onClick={() => setPairingOpen(true)}
            >
              <UtensilsCrossed size={17} aria-hidden="true" />
              <span>Conseil d’accord</span>
            </button>
            <Link className="button button-primary header-add" to="/vins/nouveau">
              <Plus size={18} aria-hidden="true" />
              <span>Ajouter un vin</span>
            </Link>
            <button
              className="header-logout-button header-lock"
              type="button"
              aria-label="Verrouiller la cave"
              title="Verrouiller la cave"
              onClick={() => void lockSecureReplica()}
            >
              <LockKeyhole size={18} aria-hidden="true" />
            </button>
            <button
              className="header-logout-button header-forget"
              type="button"
              aria-label="Oublier cet appareil"
              title="Oublier cet appareil"
              onClick={() => void handleForgetDevice()}
            >
              <Trash2 size={17} aria-hidden="true" />
            </button>
            <button
              className="mobile-menu-toggle"
              type="button"
              aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
              onClick={() => setMobileMenuOpen((value) => !value)}
            >
              {mobileMenuOpen ? <X size={21} aria-hidden="true" /> : <Menu size={21} aria-hidden="true" />}
            </button>
          </nav>
        </div>
        <nav
          id="mobile-navigation"
          className={`mobile-navigation ${mobileMenuOpen ? "open" : ""}`}
          aria-label="Navigation mobile"
          aria-hidden={!mobileMenuOpen}
        >
          <Link
            className={mobileInventoryActive ? "active" : ""}
            to="/"
            aria-current={mobileInventoryActive ? "page" : undefined}
            tabIndex={mobileMenuOpen ? 0 : -1}
          >
            <List size={18} aria-hidden="true" /> Inventaire
          </Link>
          <Link
            className={`mobile-nav-exhausted ${pathname === "/epuises" ? "active" : ""}`}
            to="/epuises"
            aria-current={pathname === "/epuises" ? "page" : undefined}
            tabIndex={mobileMenuOpen ? 0 : -1}
          >
            <WineOff size={18} aria-hidden="true" /> Épuisés
          </Link>
          <Link className={locationsActive ? "active" : ""} to="/emplacements" tabIndex={mobileMenuOpen ? 0 : -1}>
            <MapPin size={18} aria-hidden="true" /> Emplacements
          </Link>
          {onInventory && onExport ? (
            <button type="button" tabIndex={mobileMenuOpen ? 0 : -1} onClick={() => { setMobileMenuOpen(false); onExport(); }} disabled={exporting}>
              <Download size={18} aria-hidden="true" /> {exporting ? "Préparation…" : "Exporter mes données"}
            </button>
          ) : null}
          <button
            type="button"
            tabIndex={mobileMenuOpen ? 0 : -1}
            onClick={() => {
              setMobileMenuOpen(false);
              setPairingOpen(true);
            }}
          >
            <UtensilsCrossed size={18} aria-hidden="true" /> Conseil d’accord
          </button>
          <Link className="mobile-nav-add" to="/vins/nouveau" tabIndex={mobileMenuOpen ? 0 : -1}>
            <Plus size={18} aria-hidden="true" /> Ajouter un vin
          </Link>
          <button
            type="button"
            tabIndex={mobileMenuOpen ? 0 : -1}
            onClick={() => void lockSecureReplica()}
          >
            <LockKeyhole size={18} aria-hidden="true" /> Verrouiller
          </button>
          <button
            className="mobile-forget-device"
            type="button"
            tabIndex={mobileMenuOpen ? 0 : -1}
            onClick={() => void handleForgetDevice()}
          >
            <Trash2 size={18} aria-hidden="true" /> Oublier cet appareil
          </button>
        </nav>
      </header>
      <OfflineStatus />
      <main id="contenu">{children}</main>
      {onInventory ? (
        <Link className="mobile-add" to="/vins/nouveau">
          <Plus size={21} aria-hidden="true" />
          Ajouter un vin
        </Link>
      ) : null}
      <PairingAdviceModal open={pairingOpen} onClose={() => setPairingOpen(false)} />
    </div>
  );
}
